/**
 * The bridge between the active LAYOUT and the host's top bar.
 *
 * Every host renders the one shared `EndUserTopBar` (title, Apps, Search,
 * Work List, notifications, the user menu) OUTSIDE the navigation shell — the
 * platform above `RunNavShell`, the downloaded runtime around every route. A
 * layout still needs a say in that bar: the mega-menu puts its modules in it,
 * the macOS desktop its menu bar, full screen hides it, and the user menu
 * offers the layout switch. So the host wraps BOTH in `ShellChromeProvider`,
 * the shell publishes into it, and the bar reads from it. No provider ⇒ the
 * bar renders exactly as before.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { LayoutDescriptor, LayoutId } from './config'

export interface ShellChromeSlots {
  /** Rendered after the app title / Apps pill. */
  left?: ReactNode
  /** Rendered in the bar's flexible middle. */
  center?: ReactNode
  /** Rendered at the start of the right-hand group (before Search). */
  right?: ReactNode
  /** Full screen: the bar is not drawn at all. */
  hidden?: boolean
  /** The user menu's Layout choice. */
  layout?: { value: LayoutId; options: LayoutDescriptor[]; onChange: (id: LayoutId) => void }
  /** Asked before signing out (dirty open editors). False = stay signed in. */
  beforeSignOut?: () => Promise<boolean> | boolean
  /** Signed out: drop what must not outlive the session (editor snapshots). */
  afterSignOut?: () => void
}

interface ChromeCtx {
  slots: ShellChromeSlots
  publish: (slots: ShellChromeSlots | null) => void
}

const Ctx = createContext<ChromeCtx | null>(null)

export function ShellChromeProvider({ children }: { children: ReactNode }) {
  const [slots, setSlots] = useState<ShellChromeSlots>({})
  // Shallow-equal publications are dropped, so a layout re-rendering because
  // this very context changed cannot loop (layouts memoize their slots).
  const publish = useCallback((s: ShellChromeSlots | null) => setSlots((prev) => {
    const next = s || {}
    const keys = new Set([...Object.keys(prev), ...Object.keys(next)]) as Set<keyof ShellChromeSlots>
    for (const k of keys) if (prev[k] !== next[k]) return next
    return prev
  }), [])
  const value = useMemo(() => ({ slots, publish }), [slots, publish])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/** What the active layout published (empty without a provider). */
export function useShellChrome(): ShellChromeSlots {
  return useContext(Ctx)?.slots || {}
}

/** True when a chrome provider is mounted above (the host's bar listens). */
export function useHasShellChrome(): boolean {
  return useContext(Ctx) !== null
}

/** A layout's way to publish into the host bar; cleared on unmount. */
export function usePublishShellChrome(slots: ShellChromeSlots): void {
  const ctx = useContext(Ctx)
  const publish = ctx?.publish
  useEffect(() => {
    if (!publish) return undefined
    publish(slots)
    return undefined
  }, [publish, slots])
  useEffect(() => () => { publish?.(null) }, [publish])
}
